import type { ReactNode } from 'react';
import { cx } from './cx.ts';

/**
 * Label + control + message composition. Labels are ALWAYS visible at
 * `label` size in `paper-mid` — the legacy ghost-label pattern (border
 * token as text color) is banned (DESIGN.md §3).
 *
 * Contract: pass the control's `id` as `fieldId`; when rendering an
 * error also set `aria-describedby={`${fieldId}-error`}` on the control.
 */
interface Props {
  readonly label: ReactNode;
  readonly fieldId: string;
  readonly required?: boolean;
  readonly hint?: ReactNode;
  readonly error?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}

export function FormField({
  label,
  fieldId,
  required = false,
  hint,
  error,
  children,
  className,
}: Props): JSX.Element {
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label htmlFor={fieldId} className="text-label text-paper-mid">
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-bad">
            *
          </span>
        ) : null}
      </label>
      {children}
      {error !== undefined && error !== null ? (
        <p id={`${fieldId}-error`} role="alert" className="m-0 text-label text-bad">
          {error}
        </p>
      ) : hint !== undefined && hint !== null ? (
        <p className="m-0 text-label text-paper-low">{hint}</p>
      ) : null}
    </div>
  );
}
